import { useMutation, useQuery } from "@apollo/client";
import githubLogo from "@static/img/github.png";
import linearLogo from "@static/img/linear.png";
import salesforceLogo from "@static/img/salesforce.png";
import slackLogo from "@static/img/slack.png";
import teamsLogo from "@static/img/teams-64.png";
import youtrackLogo from "@static/img/youtrack.svg";
import { useCallback, useMemo, useState } from "react";

import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import { assertNonNull } from "@helpers/helpers";

import {
  GetMergeDevIntegrationsQuery,
  GetMergeDevIntegrationsQueryVariables,
  GetOrganizationIntegrationsQuery,
  GetOrganizationIntegrationsQueryVariables,
  GetSlackConfigurationQuery,
  GetSlackConfigurationQueryVariables,
  IntegrationTypes,
} from "../../../types/graphql-schema";
import getMergeDevIntegrationsQuery from "../graphql/get-merge-dev-integrations";
import getOrganizationIntegrations from "../graphql/get-organization-integrations";
import getSlackConfiguration from "../graphql/get-slack-configuration";
import removeGithubFromOrganizationMutation from "../graphql/remove-github-from-organization-mutation";
import removeMergeDevIntegrationMutation from "../graphql/remove-merge-dev-integration";
import removeSlackIntegrationMutation from "../graphql/remove-slack-integration-mutation";
import removeYouTrackIntegrationMutation from "../graphql/remove-youtrack-integration-mutation";
import unlinkOrganizationFromIntegrationMutation from "../graphql/unlink-organization-from-integration-mutation";
import { getNativeIntegrationsConfig } from "./integrations/integrations-config";
import IntegrationsConnectedTable from "./integrations/integrations-connected-table";
import { IntegrationsMarketMerge } from "./integrations/integrations-market-merge";
import { IntegrationsMarketNative } from "./integrations/integrations-market-native";
import {
  ConnectedIntegration,
  MergeIntegration,
  NativeIntegration,
} from "./integrations/integrations-types";
import SlackPermissionsDialog, {
  type SlackPermissions,
} from "./slack-permissions-dialog";

const Integrations = ({ organization }: { organization: any }) => {
  const label = useLabel();
  const [showSlackPermissionsDialog, setShowSlackPermissionsDialog] =
    useState(false);
  const [slackPermissions, setSlackPermissions] = useState<SlackPermissions>({
    notifications: false,
    bot: false,
    ingest: false,
  });
  const slackConnectUrl = useMemo(
    () =>
      `/sso/slack?next=/settings/organization/${organization.id}/integrations&org=${organization.id}`,
    [organization.id],
  );

  // ---------------------------------------------------------------------------
  // Queries and mutations for connected integrations
  // ---------------------------------------------------------------------------
  const { data: orgIntegrationsData, refetch: refetchOrgIntegrations } =
    useQuery<
      GetOrganizationIntegrationsQuery,
      GetOrganizationIntegrationsQueryVariables
    >(getOrganizationIntegrations, {
      variables: { organizationId: organization.id },
    });

  const { data: mergeIntegrationsData, refetch: refetchMergeIntegrations } =
    useQuery<
      GetMergeDevIntegrationsQuery,
      GetMergeDevIntegrationsQueryVariables
    >(getMergeDevIntegrationsQuery, {
      variables: { organizationId: organization.id },
    });
  const { data: slackConfigData } = useQuery<
    GetSlackConfigurationQuery,
    GetSlackConfigurationQueryVariables
  >(getSlackConfiguration, {
    variables: { organizationId: organization.id },
  });

  const [unlinkOrganizationFromIntegration] = useMutation(
    unlinkOrganizationFromIntegrationMutation,
  );
  const [removeSlackIntegration] = useMutation(removeSlackIntegrationMutation);
  const [removeGithub] = useMutation(removeGithubFromOrganizationMutation);
  const [removeYouTrackIntegration] = useMutation(
    removeYouTrackIntegrationMutation,
    {
      refetchQueries: ["getOrganizationSettings"],
      onCompleted: () => {
        successNotificationVar({ title: "YouTrack integration removed" });
      },
    },
  );
  const [removeMergeDevIntegration] = useMutation(
    removeMergeDevIntegrationMutation,
    {
      onCompleted: () => {
        successNotificationVar({ title: "Integration removed" });
        handleRefetch();
      },
    },
  );

  // ---------------------------------------------------------------------------
  // Refetch handler for child components
  // ---------------------------------------------------------------------------
  const handleRefetch = useCallback(() => {
    refetchOrgIntegrations();
    refetchMergeIntegrations();
  }, [refetchOrgIntegrations, refetchMergeIntegrations]);

  // ---------------------------------------------------------------------------
  // Build connected native integrations list
  // ---------------------------------------------------------------------------
  const connectedNativeIntegrations: NativeIntegration[] = useMemo(() => {
    const nativeIntegrationsConfig = getNativeIntegrationsConfig(
      organization,
      label,
    );
    const result: NativeIntegration[] = [];
    const slackLastIndexedTs = (() => {
      const channels =
        slackConfigData?.organization?.slackIndexingConfiguration?.channels ||
        [];
      const timestamps = channels
        .map((channel) => channel?.lastIndexedTs)
        .filter((ts): ts is string => Boolean(ts))
        .map((ts) => {
          const date = new Date(ts);
          return Number.isNaN(date.getTime())
            ? null
            : { ts, time: date.getTime() };
        })
        .filter((item): item is { ts: string; time: number } => Boolean(item));
      if (!timestamps.length) {
        return undefined;
      }
      return timestamps.reduce((latest, current) =>
        current.time > latest.time ? current : latest,
      ).ts;
    })();

    const salesforceIntegrations =
      orgIntegrationsData?.organization?.integrations.edges
        .filter(
          (edge) => edge?.node?.integrationType === IntegrationTypes.Salesforce,
        )
        .map((edge) => assertNonNull(edge?.node)) ?? [];
    const linearIntegrations =
      orgIntegrationsData?.organization?.integrations.edges
        .filter(
          (edge) => edge?.node?.integrationType === IntegrationTypes.Linear,
        )
        .map((edge) => assertNonNull(edge?.node)) ?? [];

    if (organization.hasSlackConnection) {
      result.push({
        id: "slack",
        type: "native",
        name: "Slack",
        slug: "slack",
        logo: slackLogo,
        description: nativeIntegrationsConfig.slack.description,
        isConnected: true,
        connectedAt: slackLastIndexedTs,
        configureUrl: waffle.flag_is_active("new-slack-settings")
          ? `/settings/organization/${organization.id}/slack`
          : undefined,
        onConnect: () => {},
        onDisconnect: () => {
          removeSlackIntegration({
            variables: { organizationId: organization.id },
          });
        },
      });
    }

    if (salesforceIntegrations.length > 0) {
      result.push({
        id: "salesforce",
        type: "native",
        name: "Salesforce",
        slug: "salesforce",
        logo: salesforceLogo,
        description: nativeIntegrationsConfig.salesforce.description,
        isConnected: true,
        connectedAt: salesforceIntegrations[0]?.created,
        onConnect: () => {},
        onDisconnect: () => {
          if (salesforceIntegrations[0]?.id) {
            unlinkOrganizationFromIntegration({
              variables: {
                organizationIntegrationId: salesforceIntegrations[0].id,
              },
            });
          }
        },
      });
    }

    if (organization.hasTeamsTranscriptConnection) {
      result.push({
        id: "teams",
        type: "native",
        name: "Teams",
        slug: "teams",
        logo: teamsLogo,
        description: nativeIntegrationsConfig.teams.description,
        isConnected: true,
        onConnect: () => {},
      });
    }

    if (organization.hasGithubInstallation) {
      result.push({
        id: "github",
        type: "native",
        name: "GitHub",
        slug: "github",
        logo: githubLogo,
        description: nativeIntegrationsConfig.github.description,
        isConnected: true,
        onConnect: () => {},
        onDisconnect: () => {
          removeGithub({
            variables: { organizationId: organization.id },
            onCompleted: () => {
              successNotificationVar({ title: "GitHub integration removed" });
            },
          });
        },
      });
    }

    if (linearIntegrations.length > 0) {
      result.push({
        id: "linear",
        type: "native",
        name: "Linear",
        slug: "linear",
        logo: linearLogo,
        description: nativeIntegrationsConfig.linear.description,
        isConnected: true,
        connectedAt: linearIntegrations[0]?.created,
        onConnect: () => {},
        onDisconnect: () => {
          if (linearIntegrations[0]?.id) {
            unlinkOrganizationFromIntegration({
              variables: {
                organizationIntegrationId: linearIntegrations[0].id,
              },
            });
          }
        },
      });
    }

    if (organization.hasYoutrackIntegration) {
      result.push({
        id: "youtrack",
        type: "native",
        name: "YouTrack",
        slug: "youtrack",
        logo: youtrackLogo,
        description: nativeIntegrationsConfig.youtrack.description,
        isConnected: true,
        onConnect: () => {},
        onDisconnect: () => {
          removeYouTrackIntegration({
            variables: { organizationId: organization.id },
          });
        },
      });
    }

    return result;
  }, [
    organization,
    label,
    orgIntegrationsData,
    slackConfigData,
    removeSlackIntegration,
    removeGithub,
    removeYouTrackIntegration,
    unlinkOrganizationFromIntegration,
  ]);

  // ---------------------------------------------------------------------------
  // Build connected merge integrations list
  // ---------------------------------------------------------------------------
  const connectedMergeIntegrations: MergeIntegration[] = useMemo(
    () =>
      (mergeIntegrationsData?.mergeDevIntegrations ?? [])
        .filter((i): i is NonNullable<typeof i> => i !== null)
        .map((integration) => ({
          id: integration.id,
          type: "merge" as const,
          name:
            integration.integrationDisplayName ||
            integration.integrationName ||
            "Unknown",
          slug: integration.integrationName || "",
          logo: integration.iconUrl || integration.fullImageUrl || "",
          category: integration.category,
          connectedAt: integration.createdAt,
        })),
    [mergeIntegrationsData],
  );

  const allConnectedIntegrations: ConnectedIntegration[] = useMemo(
    () => [...connectedNativeIntegrations, ...connectedMergeIntegrations],
    [connectedNativeIntegrations, connectedMergeIntegrations],
  );

  // ---------------------------------------------------------------------------
  // Disconnect handler
  // ---------------------------------------------------------------------------
  const handleDisconnect = useCallback(
    (integration: ConnectedIntegration) => {
      if (integration.type === "native" && integration.onDisconnect) {
        integration.onDisconnect();
      } else if (integration.type === "merge") {
        removeMergeDevIntegration({
          variables: { integrationId: integration.id },
        });
      }
    },
    [removeMergeDevIntegration],
  );

  const getExistingSlackPermissions = useCallback((): SlackPermissions => {
    const config = slackConfigData?.organization?.slackIndexingConfiguration as
      | {
          hasNotificationPermissions?: boolean;
          hasBotPermissions?: boolean;
          hasDataIngestionPermissions?: boolean;
        }
      | undefined;
    return {
      notifications: config?.hasNotificationPermissions ?? false,
      bot: config?.hasBotPermissions ?? false,
      ingest: config?.hasDataIngestionPermissions ?? false,
    };
  }, [slackConfigData]);

  // ---------------------------------------------------------------------------
  // Slack connect handler
  // ---------------------------------------------------------------------------
  const handleSlackConnect = useCallback(() => {
    setSlackPermissions(getExistingSlackPermissions());
    setShowSlackPermissionsDialog(true);
  }, [getExistingSlackPermissions]);

  const buildSlackConnectUrl = useCallback(
    (permissions: SlackPermissions) => {
      const perms = Object.entries(permissions)
        .filter(([, enabled]) => enabled)
        .map(([key]) => key)
        .join(",");
      const separator = slackConnectUrl.includes("?") ? "&" : "?";
      return perms.length > 0
        ? `${slackConnectUrl}${separator}perms=${encodeURIComponent(perms)}`
        : slackConnectUrl;
    },
    [slackConnectUrl],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div>
      <h2 className="text-2xl pb-4 font-medium text-gray-800">Integrations</h2>

      {/* Connected Integrations Table */}
      <IntegrationsConnectedTable
        integrations={allConnectedIntegrations}
        onDisconnect={handleDisconnect}
        onRefetch={handleRefetch}
      />

      {/* Marketplace Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <IntegrationsMarketNative
          organization={organization}
          connectedNativeIntegrations={connectedNativeIntegrations}
          onRefetch={handleRefetch}
          onSlackConnect={handleSlackConnect}
        />
        <IntegrationsMarketMerge
          organization={organization}
          connectedMergeIntegrations={connectedMergeIntegrations}
          onRefetch={handleRefetch}
        />
      </div>
      <SlackPermissionsDialog
        open={showSlackPermissionsDialog}
        initialPermissions={slackPermissions}
        onClose={() => setShowSlackPermissionsDialog(false)}
        onContinue={(permissions) => {
          setSlackPermissions(permissions);
          setShowSlackPermissionsDialog(false);
          window.location.href = buildSlackConnectUrl(permissions);
        }}
      />
    </div>
  );
};

export default Integrations;
